import {
  RootContextSymbol,
  TranslationContext,
  assert,
  getContext,
  getMergedContext,
  useFluent,
  warn
} from "./chunk-YCXXVQOC.js";

// src/index.ts
import { isVue3, shallowRef } from "vue-demi";

// src/vue/directive.ts
import { watchEffect } from "vue-demi";

// src/vue/dom.ts
var LOCALIZABLE_ATTRIBUTES = {
  global: ["title", "aria-label", "aria-valuetext", "aria-moz-hint"],
  a: ["download"],
  area: ["download", "alt"],
  // value is special-cased in isAttrNameLocalizable
  input: ["alt", "placeholder"],
  menuitem: ["label"],
  menu: ["label"],
  optgroup: ["label"],
  option: ["label"],
  track: ["label"],
  img: ["alt"],
  textarea: ["placeholder"],
  th: ["abbr"]
};
function isAttrNameLocalizable(name, element, explicitlyAllowed = []) {
  if (explicitlyAllowed.includes(name))
    return true;
  const attrName = name.toLowerCase();
  const elemName = element.localName;
  if (LOCALIZABLE_ATTRIBUTES.global.includes(attrName))
    return true;
  if (LOCALIZABLE_ATTRIBUTES[elemName] == null)
    return false;
  if (LOCALIZABLE_ATTRIBUTES[elemName].includes(attrName))
    return true;
  if (element.namespaceURI === "http://www.w3.org/1999/xhtml" && elemName === "input" && attrName === "value") {
    const type = element.type.toLowerCase();
    if (type === "submit" || type === "button" || type === "reset")
      return true;
  }
  return false;
}

// src/vue/directive.ts
function translate(el, fluent, binding) {
  const key = binding.arg;
  if (key === void 0) {
    warn("v-t directive is missing arg with translation key");
    return;
  }
  const translation = fluent.formatWithAttrs(key, binding.value);
  if (translation.hasValue)
    el.textContent = translation.value;
  const allowedAttrs = Object.keys(binding.modifiers);
  for (const [attr, attrValue] of Object.entries(translation.attributes)) {
    if (isAttrNameLocalizable(attr, el, allowedAttrs))
      el.setAttribute(attr, attrValue);
    else
      warn(`Attribute '${attr}' on element <${el.tagName.toLowerCase()}> is not localizable. Remove it from the translation. Translation key: ${key}`);
  }
}
function createVue3Directive(rootContext) {
  return {
    mounted(el, binding) {
      watchEffect(() => {
        const context = getContext(rootContext, binding.instance);
        translate(el, context, binding);
      });
    },
    updated(el, binding) {
      const context = getContext(rootContext, binding.instance);
      translate(el, context, binding);
    },
    getSSRProps(binding) {
      const context = getContext(rootContext, binding.instance);
      const key = binding.arg;
      if (key === void 0) {
        warn("v-t directive is missing arg with translation key");
        return {};
      }
      const translation = context.formatWithAttrs(key, binding.value);
      const allowedAttrs = Object.keys(binding.modifiers);
      const attrs = {};
      for (const [attr, attrValue] of Object.entries(translation.attributes)) {
        if (isAttrNameLocalizable(attr, {}, allowedAttrs))
          attrs[attr] = attrValue;
      }
      return attrs;
    }
  };
}
function createVue2Directive(rootContext) {
  return {
    bind(el, binding, vnode) {
      const context = getContext(rootContext, vnode.context);
      translate(el, context, binding);
    },
    update(el, binding, vnode) {
      const context = getContext(rootContext, vnode.context);
      translate(el, context, binding);
    }
  };
}

// src/vue/component.ts
import {
  computed,
  defineComponent,
  getCurrentInstance,
  h,
  isVue2
} from "vue-demi";

// src/util/camelize.ts
var camelizeRE = /-(\w)/g;
function camelize(str) {
  return str.replace(camelizeRE, (_, c) => c.toUpperCase());
}

// src/vue/component.ts
function getParentWithFluent(instance) {
  const parent = instance == null ? void 0 : instance.$parent;
  const target = parent == null ? void 0 : parent.$options;
  if (target != null && target.fluent == null)
    return getParentWithFluent(parent);
  return parent;
}
var reMarkup = /<|&#?\w+;/;
function createComponent(options, rootContext) {
  return defineComponent({
    name: options.componentName,
    props: {
      path: { type: String, required: true },
      tag: { type: [String, Boolean], default: options.componentTag },
      args: { type: Object, default: () => ({}) },
      html: { type: Boolean, default: false },
      noTag: { type: Boolean, default: false }
    },
    setup(props, { slots, attrs }) {
      const instance = getCurrentInstance();
      const parent = getParentWithFluent(instance == null ? void 0 : instance.proxy);
      const fluent = getContext(rootContext, parent);
      const translation = computed(() => {
        const fluentParams = Object.assign(
          {},
          props.args,
          ...Object.keys(slots).map((key) => ({
            [key]: `\uFFFF\uFFFE${key}\uFFFF`
          }))
        );
        const result = fluent.formatWithAttrs(props.path, fluentParams);
        const camelizedAttrs = Object.fromEntries(
          Object.entries(result.attributes).map(([key, value]) => [camelize(key), value])
        );
        return {
          value: result.value,
          attributes: camelizedAttrs
        };
      });
      const insertSlots = (text) => {
        return text == null ? void 0 : text.split("\uFFFF").map(
          (text2) => text2.startsWith("\uFFFE") ? slots[text2.replace("\uFFFE", "")](translation.value.attributes) : text2
        );
      };
      const processNode = (node) => {
        if (node.nodeType === 3) {
          return insertSlots(node.nodeValue);
        } else if (node.nodeType === 1) {
          const el = node;
          return h(
            el.nodeName.toLowerCase(),
            {
              ...Object.fromEntries(
                Array.from(el.attributes).map((attr) => [attr.name, attr.value])
              )
            },
            Array.from(el.childNodes).map((node2) => processNode(node2))
          );
        }
        warn(`Unsupported node type: ${node.nodeType}. If you need support for it, please, create an issue in fluent-vue repository.`);
        return [];
      };
      const children = computed(() => {
        if (!props.html || !reMarkup.test(translation.value.value))
          return insertSlots(translation.value.value);
        const nodes = fluent.options.parseMarkup(translation.value.value);
        return nodes.map(processNode);
      });
      if (isVue2 && (props.tag === false || props.noTag))
        warn("Vue 2 requires a root element when rendering components. Please, use `tag` prop to specify the root element.");
      return () => props.tag === false || props.noTag ? children.value : h(props.tag, { ...attrs }, children.value);
    }
  });
}

// src/util/options.ts
function defaultMarkupParser(value) {
  assert(typeof DOMParser !== "undefined", "DOMParser is not available. Please provide a custom parseMarkup function.");
  const parser = new DOMParser();
  const doc = parser.parseFromString(value, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
}
function defaultWarnMissing(key) {
  warn(`Could not find translation for key [${key}]`);
}
function getWarnMissing(options) {
  if (options.warnMissing === true || options.warnMissing == null)
    return defaultWarnMissing;
  else if (options.warnMissing === false)
    return () => {
    };
  else
    return options.warnMissing;
}
function resolveOptions(options) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
  return {
    warnMissing: getWarnMissing(options),
    parseMarkup: (_a = options.parseMarkup) != null ? _a : defaultMarkupParser,
    globalFormatName: (_d = (_c = (_b = options.globals) == null ? void 0 : _b.functions) == null ? void 0 : _c.format) != null ? _d : "$t",
    globalFormatAttrsName: (_g = (_f = (_e = options.globals) == null ? void 0 : _e.functions) == null ? void 0 : _f.formatAttrs) != null ? _g : "$ta",
    directiveName: (_i = (_h = options.globals) == null ? void 0 : _h.directive) != null ? _i : "t",
    componentName: (_k = (_j = options.globals) == null ? void 0 : _j.component) != null ? _k : "i18n",
    componentTag: (_l = options.componentTag) != null ? _l : "span"
  };
}

// src/index.ts
function createFluentVue(options) {
  const bundles = shallowRef(options.bundles);
  const resolvedOptions = resolveOptions(options);
  const rootContext = new TranslationContext(bundles, resolvedOptions);
  return {
    get bundles() {
      return bundles.value;
    },
    set bundles(value) {
      bundles.value = value;
    },
    mergedWith: (extraTranslations) => {
      return getMergedContext(rootContext, extraTranslations);
    },
    format: rootContext.format.bind(rootContext),
    formatAttrs: rootContext.formatAttrs.bind(rootContext),
    formatWithAttrs: rootContext.formatWithAttrs.bind(rootContext),
    $t: rootContext.format.bind(rootContext),
    $ta: rootContext.formatAttrs.bind(rootContext),
    install(vue) {
      if (isVue3) {
        const vue3 = vue;
        vue3.provide(RootContextSymbol, rootContext);
        vue3.config.globalProperties[resolvedOptions.globalFormatName] = function(key, value) {
          return getContext(rootContext, this).format(key, value);
        };
        vue3.config.globalProperties[resolvedOptions.globalFormatAttrsName] = function(key, value) {
          return getContext(rootContext, this).formatAttrs(key, value);
        };
        vue3.directive(resolvedOptions.directiveName, createVue3Directive(rootContext));
      } else {
        const vue2 = vue;
        vue2.mixin({
          provide() {
            return {
              [RootContextSymbol]: rootContext
            };
          }
        });
        vue2.prototype[resolvedOptions.globalFormatName] = function(key, value) {
          return getContext(rootContext, this).format(key, value);
        };
        vue2.prototype[resolvedOptions.globalFormatAttrsName] = function(key, value) {
          return getContext(rootContext, this).formatAttrs(key, value);
        };
        vue2.directive(resolvedOptions.directiveName, createVue2Directive(rootContext));
      }
      vue.component(resolvedOptions.componentName, createComponent(resolvedOptions, rootContext));
    }
  };
}
export {
  createFluentVue,
  useFluent
};
